import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { showErrorAlert, showActualizadoCorrectamenteAlert } from "../../../components/molecules/ShowAlert";
import { exportarAsistenciaGrupo, getAllGrupos } from "../../../services/grupo.service";

export const DescargarAsistencia = () => {
  const navigate = useNavigate();
  const [grupos, setGrupos] = useState([]);
  const [selectedGrupos, setSelectedGrupos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrar grupos basado en la búsqueda
  const filteredGrupos = grupos.filter(grupo => {
    const searchLower = searchTerm.toLowerCase();
    return (
      grupo.nombre?.toLowerCase().includes(searchLower) ||
      grupo.docente?.nombre?.toLowerCase().includes(searchLower) ||
      grupo.modulo?.nombre?.toLowerCase().includes(searchLower) ||
      grupo.nivel?.nombre?.toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    const cargarGrupos = async () => {
      setIsLoading(true);
      try {
        const listaGrupos = await getAllGrupos();
        setGrupos(listaGrupos);
      } catch (error) {
        showErrorAlert({ text: "Error al cargar los grupos", icon: "error" });
      } finally {
        setIsLoading(false);
      }
    };
    cargarGrupos();
  }, []);

  const toggleGrupoSelection = (grupoId) => {
    setSelectedGrupos(prev => 
      prev.includes(grupoId) 
        ? prev.filter(id => id !== grupoId) 
        : [...prev, grupoId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedGrupos(prev => 
      prev.length === grupos.length ? [] : grupos.map(g => g.idGrupo)
    );
  };

  const removeGrupo = (grupoId) => {
    setSelectedGrupos(prev => prev.filter(id => id !== grupoId));
  };

  const clearSelection = () => {
    setSelectedGrupos([]);
  };

  const handleExportar = async () => {
    if (selectedGrupos.length === 0) {
      showErrorAlert({ text: "Debe seleccionar al menos un grupo", icon: "error" });
      return;
    }

    try {
      setIsLoading(true);
      await Promise.all(selectedGrupos.map(grupoId => exportarAsistenciaGrupo(grupoId)));
       showActualizadoCorrectamenteAlert({ 
         text: `Listas de asistencia de ${selectedGrupos.length} grupo(s) generadas exitosamente`, 
         icon: "success" 
       });
      clearSelection();
    } catch (err) {
       showErrorAlert({ 
         text: `Error al generar listas de asistencia: ${err.message}`, 
         icon: "error" 
       });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContenedorPageTemplate titulo="Descargar Reportes de Asistencia">
      {isLoading && grupos.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-base text-gray-600">Cargando lista de grupos...</p>
        </div>
      ) : (
        <div className="container mx-auto px-4 py-6 max-w-6xl">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            
            {/* Header elegante con tema de asistencia */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-white flex items-center">
                    <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Generar Listas de Asistencia
                  </h1>
                  <p className="text-base text-emerald-100 mt-1">Selecciona grupos y genera listas de asistencia para imprimir</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {grupos.length} grupos
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Diseño elegante de dos columnas */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* Columna Izquierda: Lista de Grupos Disponibles */}
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-gray-800 flex items-center">
                        <svg className="w-6 h-6 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Grupos para Lista de Asistencia
                      </h2>
                      <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
                        {grupos.length} total
                      </span>
                    </div>

                    {/* Barra de búsqueda elegante */}
                    <div className="relative mb-4">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                        </svg>
                      </div>
                      <input
                        type="text"
                        placeholder="Buscar grupos para lista de asistencia..."
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-base shadow-sm transition-all"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                    </div>

                    {/* Botón seleccionar todos */}
                    <div className="flex items-center justify-between mb-4">
                      <label className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedGrupos.length === grupos.length && grupos.length > 0}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 mr-2"
                        />
                        {selectedGrupos.length === grupos.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                      </label>
                    </div>

                    {/* Lista de grupos con diseño elegante */}
                    <div className="max-h-96 overflow-y-auto space-y-2 pr-2">
                      {filteredGrupos.length ? (
                        filteredGrupos.map(g => (
                          <div
                            key={g.idGrupo}
                            onClick={() => toggleGrupoSelection(g.idGrupo)}
                            className={`group p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                              selectedGrupos.includes(g.idGrupo) 
                                ? 'border-emerald-500 bg-emerald-50 shadow-md' 
                                : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-sm'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`mt-1 h-5 w-5 border-2 rounded-full flex items-center justify-center transition-all ${
                                  selectedGrupos.includes(g.idGrupo) 
                                    ? 'bg-emerald-600 border-emerald-600' 
                                    : 'border-gray-300 group-hover:border-emerald-400'
                                }`}
                              >
                                {selectedGrupos.includes(g.idGrupo) && (
                                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-base font-semibold text-gray-800 truncate">{g.nombre}</h3>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <span className="px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                    {g.modulo?.nombre || 'Sin módulo'}
                                  </span>
                                  <span className="px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                                    {g.nivel?.nombre || 'Sin nivel'}
                                  </span>
                                </div>
                                {g.docente?.nombre && (
                                  <p className="text-sm text-gray-600 mt-1 truncate">
                                    👨‍🏫 {g.docente.nombre} {g.docente.apellido}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709M15 6.709A7.962 7.962 0 0112 5c-2.34 0-4.29 1.009-5.824 2.709"/>
                          </svg>
                          <p className="text-base">
                            {searchTerm ? 'No se encontraron grupos' : 'No hay grupos disponibles'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Columna Derecha: Grupos Seleccionados + Botón de Descarga */}
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-gray-800 flex items-center">
                        <svg className="w-6 h-6 mr-2 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Listas de Asistencia Seleccionadas
                      </h2>
                      {selectedGrupos.length > 0 && (
                        <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                          {selectedGrupos.length} lista{selectedGrupos.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {selectedGrupos.length > 0 ? (
                      <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                        {selectedGrupos.map(id => {
                          const grupo = grupos.find(g => g.idGrupo === id);
                          return (
                            <div
                              key={id}
                              className="flex items-center justify-between bg-white rounded-lg border border-amber-200 p-3 shadow-sm"
                            >
                              <div className="flex-1 min-w-0">
                                <h3 className="text-base font-semibold text-gray-800 truncate">{grupo?.nombre}</h3>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    {grupo?.modulo?.nombre || 'Sin módulo'}
                                  </span>
                                  <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                    {grupo?.nivel?.nombre || 'Sin nivel'}
                                  </span>
                                </div>
                              </div>
                              <button 
                                onClick={() => removeGrupo(id)} 
                                className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                        </svg>
                        <p className="text-base font-medium">Ninguna lista seleccionada</p>
                        <p className="text-sm mt-1">Selecciona grupos para generar listas de asistencia</p>
                      </div>
                    )}

                    {selectedGrupos.length > 0 && (
                      <button 
                        onClick={clearSelection} 
                        className="w-full mt-4 text-sm text-red-600 hover:text-red-800 font-medium py-2 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        🗑️ Limpiar selección
                      </button>
                    )}
                  </div>

                  {/* Botón de generación elegante */}
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                    <button
                      onClick={handleExportar}
                      disabled={isLoading || !selectedGrupos.length}
                      className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center transition-all duration-300 ${
                        isLoading
                          ? 'bg-emerald-400 text-white cursor-wait shadow-lg'
                          : !selectedGrupos.length
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-2 border-dashed border-gray-300'
                          : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg hover:from-emerald-700 hover:to-teal-700 hover:shadow-xl transform hover:-translate-y-1'
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generando listas...
                        </>
                      ) : !selectedGrupos.length ? (
                        <>
                          <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          Selecciona grupos para generar listas
                        </>
                      ) : selectedGrupos.length > 1 ? (
                        <>
                          <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          📋 Generar {selectedGrupos.length} listas de asistencia
                        </>
                      ) : (
                        <>
                          <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          📋 Generar lista de asistencia
                        </>
                      )}
                    </button>
                    
                    {selectedGrupos.length > 0 && (
                      <p className="text-center text-sm text-emerald-600 mt-3 font-medium">
                        ✅ {selectedGrupos.length} lista{selectedGrupos.length !== 1 ? 's' : ''} de asistencia lista{selectedGrupos.length !== 1 ? 's' : ''} para generar
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </ContenedorPageTemplate>
  );
};